// Paging for the tracker grid.
//
// The server returns engagements one page at a time (newest-updated first) and
// says how many match in total. The client appends every page onto a single
// grid object, so client grouping, column filters, sorting and the CSV export
// all work over the full result set rather than the first page.

// Rows per request.
export const PAGE_SIZE = 500;

// Pages keep loading on their own until the grid holds this many rows. Past
// it the user chooses "load more" / "load all" — an unfiltered tracker for a
// big firm can hold 25k+ engagements, which nobody wants pulled by default.
export const AUTO_LOAD_MAX = 5000;

/**
 * Append one page from the server onto the grid loaded so far.
 *
 * `grid` is null for the first page. Rows are de-duplicated by engagement id
 * (an engagement edited between two requests moves to the top of the server
 * order and could otherwise appear twice). `fetched` counts rows received
 * from the server, duplicates included, and is the offset of the next page.
 */
export function mergeGridPage(grid, page) {
	if (!page) return grid;
	const pageRows = page.rows || [];
	if (!grid) {
		return {
			...page,
			rows: pageRows,
			fetched: (page.offset || 0) + pageRows.length,
			returned_count: pageRows.length,
			has_more: !!page.has_more,
		};
	}

	const seen = new Set((grid.rows || []).map(r => r.engagement_id));
	const added = pageRows.filter(r => !seen.has(r.engagement_id));
	const rows = (grid.rows || []).concat(added);

	// Datasource labels are resolved per page (only for the ids on that page), so union them.
	const datasource_labels = { ...(grid.datasource_labels || {}) };
	Object.keys(page.datasource_labels || {}).forEach(fieldId => {
		datasource_labels[fieldId] = { ...(datasource_labels[fieldId] || {}), ...page.datasource_labels[fieldId] };
	});

	// Backoffice roll-ups arrive per page too; the extra (backoffice-only) clients come with the first page.
	let backoffice = grid.backoffice || null;
	if (page.backoffice) {
		const prev = grid.backoffice || {};
		backoffice = {
			...page.backoffice,
			by_client: { ...(prev.by_client || {}), ...(page.backoffice.by_client || {}) },
			extra_clients: prev.extra_clients && prev.extra_clients.length ? prev.extra_clients : page.backoffice.extra_clients || [],
		};
	}

	return {
		...grid,
		rows,
		datasource_labels,
		backoffice,
		total_count: page.total_count != null ? page.total_count : grid.total_count,
		returned_count: rows.length,
		fetched: (grid.fetched || 0) + pageRows.length,
		has_more: !!page.has_more,
	};
}
